import React from 'react';
import { motion } from 'framer-motion';
import { Search, X, Leaf, Sprout, Star, LayoutGrid } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';

const SearchBar = ({ searchQuery, onSearchChange, activeFilter, onFilterChange }) => {
  const { t } = useLanguage();
  const { isDark } = useTheme();

  const filters = [
    { id: 'all', label: t('filter_all'), icon: LayoutGrid },
    { id: 'vegan', label: t('filter_vegan'), icon: Sprout },
    { id: 'vegetarian', label: t('filter_vegetarian'), icon: Leaf },
    { id: 'top', label: t('filter_top'), icon: Star }
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <div className="flex flex-col sm:flex-row gap-4">
        {/* Search Input */}
        <div className="relative flex-1">
          <Search className={`absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 ${
            isDark ? 'text-white/50' : 'text-gray-400'
          }`} />
          <input
            type="text"
            data-testid="search-input"
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={t('search_placeholder')}
            className={`w-full pl-12 pr-10 py-3 border rounded-full focus:outline-none focus:border-[#008000]/50 focus:ring-2 focus:ring-[#008000]/20 transition-all duration-300 ${
              isDark 
                ? 'bg-white/5 border-white/10 text-white placeholder:text-white/40' 
                : 'bg-black/5 border-black/10 text-gray-900 placeholder:text-gray-400'
            }`}
          />
          {searchQuery && (
            <button
              data-testid="clear-search-btn"
              onClick={() => onSearchChange('')}
              className={`absolute right-4 top-1/2 transform -translate-y-1/2 transition-colors ${
                isDark ? 'text-white/50 hover:text-white' : 'text-gray-400 hover:text-gray-600'
              }`}
            >
              <X size={18} />
            </button>
          )}
        </div>

        {/* Filter Buttons */}
        <div className="flex gap-2 flex-wrap">
          {filters.map((filter) => {
            const IconComponent = filter.icon;
            const isActive = activeFilter === filter.id;
            
            return (
              <motion.button
                key={filter.id}
                data-testid={`filter-${filter.id}`}
                onClick={() => onFilterChange(filter.id)}
                className={`
                  flex items-center gap-2 whitespace-nowrap px-4 py-2.5 rounded-full 
                  border text-sm font-medium transition-all duration-300
                  ${isActive 
                    ? 'bg-[#C8102E] text-white border-[#C8102E]' 
                    : isDark
                      ? 'bg-white/5 text-white/70 border-white/10 hover:bg-white/10 hover:text-white'
                      : 'bg-black/5 text-gray-600 border-black/10 hover:bg-black/10 hover:text-gray-900'
                  }
                `}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {IconComponent && <IconComponent size={16} />}
                <span>{filter.label}</span>
              </motion.button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default SearchBar;
